import React, { useState } from 'react';
import { 
  Plus, Pencil, Trash2, Download, Settings, Search, Users, User,
  Phone, Building, MapPin, MessageSquare, Circle, GripVertical, Check,
  FileText, Calendar, DollarSign, Filter, ClipboardList, Copy, ExternalLink, CheckCircle2,
  Save, RotateCcw, ChevronDown, X, AlertTriangle, Clock, RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import {
  updateCrmContact, importContactsFromQuotes, createPipelineStatus, 
  updatePipelineStatus, deletePipelineStatus, deleteCrmContact, markContactAsContacted
} from '../services/api';

// Helper functions for WhatsApp
const openWhatsAppDesktop = (phone) => {
  const cleanPhone = phone?.replace(/\D/g, '');
  if (!cleanPhone) {
    toast.error('Número de telefone inválido');
    return;
  }
  // whatsapp:// protocol opens the desktop app directly
  window.location.href = `whatsapp://send?phone=${cleanPhone}`;
};

const copyPhoneToClipboard = async (phone, name) => {
  const cleanPhone = phone?.replace(/\D/g, '');
  if (!cleanPhone) {
    toast.error('Número de telefone inválido');
    return;
  }
  try {
    await navigator.clipboard.writeText(cleanPhone);
    toast.success(`Número de ${name} copiado!`);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = cleanPhone;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    toast.success(`Número de ${name} copiado!`);
  }
};

// Helper: Calculate days without contact
const getDaysWithoutContact = (lastContactedAt) => {
  if (!lastContactedAt) return null; // Never contacted
  const lastContact = new Date(lastContactedAt);
  const today = new Date();
  const diffTime = today - lastContact;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper: Check if contact matches date filter
const matchesContactDateFilter = (contact, selectedContactFilters) => {
  if (selectedContactFilters.length === 0) return true;
  
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  
  const lastContacted = contact.last_contacted_at ? new Date(contact.last_contacted_at) : null;
  
  for (const filter of selectedContactFilters) {
    if (filter === 'today' && lastContacted && lastContacted.toDateString() === today.toDateString()) {
      return true;
    }
    if (filter === 'yesterday' && lastContacted && lastContacted.toDateString() === yesterday.toDateString()) {
      return true;
    }
    if (filter === 'week' && lastContacted && lastContacted >= weekAgo) {
      return true;
    }
    if (filter === 'old' && lastContacted && lastContacted < weekAgo) {
      return true;
    }
    if (filter === 'never' && !lastContacted) {
      return true;
    }
  }
  
  return false;
};

// Draggable Contact Card Component
const DraggableContactCard = ({ contact, sourceOptions, pipeline, onOpenContact, onDeleteContact, onCreateTask, onMarkContacted, onStatusChange }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: contact.id });

  // Check if contacted today
  const isContactedToday = () => {
    if (!contact.last_contacted_at) return false;
    const lastContact = new Date(contact.last_contacted_at);
    const today = new Date();
    return lastContact.toDateString() === today.toDateString();
  };

  // Format last contacted date
  const formatLastContacted = () => {
    if (!contact.last_contacted_at) return null;
    const lastContact = new Date(contact.last_contacted_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (lastContact.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (lastContact.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return lastContact.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  // Calculate potential value from quotes
  const potentialValue = contact.total_quotes_value || contact.potential_value || 0;
  
  // Get current status
  const currentStatus = pipeline?.find(s => s.id === contact.status_id || s.id === contact.status);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white rounded-lg p-2 shadow-sm border border-slate-100 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing touch-none ${isDragging ? 'ring-2 ring-teal-500' : ''}`}
    >
      <div className="flex items-start justify-between gap-1 mb-0.5">
        <p className="font-medium text-slate-800 text-sm truncate flex-1">{contact.name}</p>
        <div 
          className="flex items-center gap-0.5 flex-shrink-0"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Status Change Button */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowStatusMenu(!showStatusMenu);
              }}
              className="p-0.5 rounded transition-colors hover:bg-slate-100"
              title="Mudar status"
            >
              <div 
                className="w-3 h-3 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: currentStatus?.color || '#64748b' }}
              />
            </button>
            {showStatusMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 min-w-[120px]">
                {pipeline?.map(status => (
                  <button
                    key={status.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onStatusChange(contact, status.id);
                      setShowStatusMenu(false);
                    }}
                    className={`w-full flex items-center gap-2 px-2 py-1 text-xs hover:bg-slate-50 ${
                      (contact.status_id === status.id || contact.status === status.id) ? 'bg-slate-100' : ''
                    }`}
                  >
                    <div 
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="truncate">{status.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* WhatsApp Desktop Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openWhatsAppDesktop(contact.phone);
            }}
            className="p-0.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded transition-colors cursor-pointer"
            title="Abrir no WhatsApp Desktop"
            data-testid="whatsapp-desktop-btn"
          >
            <Phone className="w-3 h-3" />
          </button>
          {/* Copy Phone Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              copyPhoneToClipboard(contact.phone, contact.name);
            }}
            className="p-0.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
            title="Copiar número"
            data-testid="copy-phone-btn"
          >
            <Copy className="w-3 h-3" />
          </button>
          {/* Contacted Today Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onMarkContacted(contact);
            }}
            className={`p-0.5 rounded transition-colors cursor-pointer ${
              isContactedToday() 
                ? 'text-emerald-500 bg-emerald-50' 
                : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            title={isContactedToday() ? 'Contatado hoje' : 'Marcar como contatado hoje'}
            data-testid="mark-contacted-btn"
          >
            <CheckCircle2 className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onCreateTask(contact);
            }}
            className="p-0.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors cursor-pointer"
            title="Criar tarefa"
          >
            <ClipboardList className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onOpenContact(contact);
            }}
            className="p-0.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors cursor-pointer"
            title="Editar contato"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDeleteContact(contact);
            }}
            className="p-0.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
            title="Excluir contato"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 truncate">{contact.phone}</p>
      
      {/* Summary - compact */}
      {contact.summary && (
        <div className="mt-1 p-1.5 bg-amber-50 rounded text-xs text-amber-800 border-l-2 border-amber-400 truncate">
          {contact.summary.length > 40 ? contact.summary.substring(0, 40) + '...' : contact.summary}
        </div>
      )}
      
      {/* Days without contact warning */}
      {(() => {
        const days = getDaysWithoutContact(contact.last_contacted_at);
        if (days === null) {
          return (
            <div className="mt-1 p-1 bg-red-50 rounded text-xs text-red-600 flex items-center gap-1 border-l-2 border-red-400">
              <AlertTriangle className="w-3 h-3" />
              <span>Nunca contatado</span>
            </div>
          );
        } else if (days >= 3) {
          return (
            <div className={`mt-1 p-1 rounded text-xs flex items-center gap-1 border-l-2 ${
              days >= 7 ? 'bg-red-50 text-red-600 border-red-400' : 'bg-amber-50 text-amber-600 border-amber-400'
            }`}>
              <Clock className="w-3 h-3" />
              <span>{days} dias sem contato</span>
            </div>
          );
        }
        return null;
      })()}
      
      {/* Footer with metadata - compact */}
      <div className="flex items-center justify-between gap-1 mt-1.5 pt-1.5 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          {contact.notes?.length > 0 && (
            <span className="flex items-center gap-0.5">
              <MessageSquare className="w-2.5 h-2.5" />
              {contact.notes.length}
            </span>
          )}
          {/* Last contacted indicator */}
          {contact.last_contacted_at && (
            <span className={`flex items-center gap-0.5 ${isContactedToday() ? 'text-emerald-500 font-medium' : 'text-slate-400'}`}>
              <CheckCircle2 className="w-2.5 h-2.5" />
              {formatLastContacted()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          {potentialValue > 0 && (
            <span className="text-green-600 font-medium flex items-center gap-0.5">
              <DollarSign className="w-2.5 h-2.5" />
              {potentialValue >= 1000 ? `${(potentialValue/1000).toFixed(0)}k` : potentialValue}
            </span>
          )}
          {contact.created_at && (
            <span className="text-slate-400 flex items-center gap-0.5">
              <Calendar className="w-2.5 h-2.5" />
              {formatDate(contact.created_at)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Empty Column Droppable Placeholder
const EmptyColumnPlaceholder = ({ statusId, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: `empty-${statusId}`,
    data: { statusId }
  });

  return (
    <div 
      ref={setNodeRef}
      className={`text-center py-4 text-xs border-2 border-dashed rounded-lg ${isOver ? 'border-teal-400 bg-teal-50 text-teal-600' : 'border-slate-200 text-slate-400'}`}
    >
      {isOver ? 'Solte aqui!' : 'Arraste aqui'}
    </div>
  );
};

// Droppable Column Component
const DroppableColumn = ({ status, contacts, sourceOptions, pipeline, onOpenContact, onDeleteContact, onCreateTask, onMarkContacted, onStatusChange }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status.id,
  });

  return (
    <div 
      ref={setNodeRef}
      className={`rounded-xl p-2 transition-colors min-w-0 ${isOver ? 'bg-teal-50 ring-2 ring-teal-300' : 'bg-slate-50'}`}
    >
      <div 
        className="flex items-center justify-between mb-2 px-2 py-1 rounded-lg"
        style={{ backgroundColor: `${status.color}20` }}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <div 
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: status.color }}
          />
          <span className="font-medium text-xs truncate" style={{ color: status.color }}>
            {status.name}
          </span>
        </div>
        <span 
          className="text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: status.color, color: 'white' }}
        >
          {contacts.length}
        </span>
      </div>
      
      <SortableContext items={contacts.map(c => c.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5 min-h-[60px] max-h-[55vh] overflow-y-auto">
          {contacts.map(contact => (
            <DraggableContactCard
              key={contact.id}
              contact={contact}
              sourceOptions={sourceOptions}
              pipeline={pipeline}
              onOpenContact={onOpenContact}
              onDeleteContact={onDeleteContact}
              onCreateTask={onCreateTask}
              onMarkContacted={onMarkContacted}
              onStatusChange={onStatusChange}
            />
          ))}
          {contacts.length === 0 && (
            <EmptyColumnPlaceholder statusId={status.id} isOver={isOver} />
          )}
        </div>
      </SortableContext>
    </div>
  );
};

// Main CRM Section Component
const CrmSection = ({ contacts, stats, tags, pipeline, currentUser, onRefresh, onManualRefresh, wsConnected, onOptimisticStatusChange, onOpenContact, onEditContact, onNewContact, onPipelineUpdate, onCreateTaskFromContact }) => {
  // Load saved filters from localStorage
  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem('crm_filters');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return null;
  };

  const savedFilters = loadSavedFilters();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(savedFilters?.selectedStatuses || []);
  const [selectedContactFilters, setSelectedContactFilters] = useState(savedFilters?.selectedContactFilters || []);
  const [importing, setImporting] = useState(false);
  const [viewMode, setViewMode] = useState(savedFilters?.viewMode || 'kanban');
  const [hideEmpty, setHideEmpty] = useState(savedFilters?.hideEmpty || false);
  const [pipelineModalOpen, setPipelineModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#64748b');
  const [activeId, setActiveId] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [hasSavedFilters, setHasSavedFilters] = useState(!!savedFilters);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [contactFilterOpen, setContactFilterOpen] = useState(false);

  // Contact date filter options
  const contactFilterOptions = [
    { id: 'today', label: 'Contatados hoje', color: 'emerald' },
    { id: 'yesterday', label: 'Contatados ontem', color: 'blue' },
    { id: 'week', label: 'Última semana', color: 'purple' },
    { id: 'old', label: 'Mais de 7 dias', color: 'amber' },
    { id: 'never', label: 'Nunca contatados', color: 'red' }
  ];

  // Toggle contact date filter
  const toggleContactFilter = (filterId) => {
    setSelectedContactFilters(prev => {
      if (prev.includes(filterId)) {
        return prev.filter(id => id !== filterId);
      } else {
        return [...prev, filterId];
      }
    });
  };

  // Clear contact date filters
  const clearContactFilters = () => {
    setSelectedContactFilters([]);
  };

  // Toggle status in filter
  const toggleStatusFilter = (statusId) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusId)) {
        return prev.filter(id => id !== statusId);
      } else {
        return [...prev, statusId];
      }
    });
  };

  // Clear status selection
  const clearStatusSelection = () => {
    setSelectedStatuses([]);
  };

  // Save filters to localStorage
  const handleSaveFilters = () => {
    const filters = {
      selectedStatuses,
      selectedContactFilters,
      viewMode,
      hideEmpty
    };
    localStorage.setItem('crm_filters', JSON.stringify(filters));
    setHasSavedFilters(true);
    toast.success('Filtros salvos!');
  };

  // Clear saved filters
  const handleClearFilters = () => {
    localStorage.removeItem('crm_filters');
    setSelectedStatuses([]);
    setSelectedContactFilters([]);
    setViewMode('kanban');
    setHideEmpty(false);
    setSearchTerm('');
    setHasSavedFilters(false);
    toast.success('Filtros limpos!');
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const sourceOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'shopee', label: 'Shopee' },
    { value: 'indicacao', label: 'Indicação' },
    { value: 'site', label: 'Site' },
    { value: 'outro', label: 'Outro' }
  ];

  const getStatusById = (statusId) => {
    return pipeline.find(s => s.id === statusId) || pipeline[0];
  };

  const getContactsByStatus = (statusId) => {
    return contacts.filter(c => c.status_id === statusId || c.status === statusId);
  };

  const filteredContacts = contacts.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           c.phone.includes(searchTerm) ||
           c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           c.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatuses.length === 0 || 
           selectedStatuses.includes(c.status_id) || 
           selectedStatuses.includes(c.status);
    const matchesContactDate = matchesContactDateFilter(c, selectedContactFilters);
    return matchesSearch && matchesStatus && matchesContactDate;
  });

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Tem certeza que deseja excluir o contato "${contact.name}"?`)) return;
    try {
      await deleteCrmContact(contact.id);
      onRefresh();
    } catch (error) {
      alert('Erro ao excluir contato');
    }
  };

  const handleMarkContacted = async (contact) => {
    try {
      await markContactAsContacted(contact.id);
      toast.success(`${contact.name} marcado como contatado!`);
      onRefresh();
    } catch (error) {
      console.error('Erro ao marcar contato:', error);
      toast.error('Erro ao marcar contato');
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await importContactsFromQuotes();
      alert(result.message);
      onRefresh();
    } catch (error) {
      alert('Erro ao importar contatos');
    } finally {
      setImporting(false);
    }
  };

  const handleStatusChange = async (contact, newStatusId) => {
    // Optimistic update - immediately update UI
    if (onOptimisticStatusChange) {
      onOptimisticStatusChange(contact.id, newStatusId);
    }
    
    // Then sync with backend (in background)
    try {
      await updateCrmContact(contact.id, { 
        status_id: newStatusId,
        status: newStatusId
      });
      // No need to call onRefresh - UI already updated
    } catch (error) {
      console.error('Error updating status:', error);
      // On error, refresh to get correct state
      onRefresh();
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const contact = contacts.find(c => c.id === active.id);
    setActiveContact(contact);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveContact(null);

    if (!over) return;

    const contactId = active.id;
    const contact = contacts.find(c => c.id === contactId);
    
    if (!contact) return;

    // Check if dropped over a column (droppable), empty placeholder, or over a card
    let targetStatusId = over.id;
    const isColumn = pipeline.some(s => s.id === targetStatusId);
    
    // Check if dropped over empty column placeholder (id starts with "empty-")
    const isEmptyPlaceholder = typeof over.id === 'string' && over.id.startsWith('empty-');
    if (isEmptyPlaceholder) {
      targetStatusId = over.id.replace('empty-', '');
    }
    // If dropped over a card, find the column that card belongs to
    else if (!isColumn) {
      const targetContact = contacts.find(c => c.id === over.id);
      if (targetContact) {
        targetStatusId = targetContact.status_id || targetContact.status;
      } else {
        return; // Unknown drop target
      }
    }
    
    const currentStatusId = contact.status_id || contact.status;
    if (currentStatusId !== targetStatusId) {
      await handleStatusChange(contact, targetStatusId);
    }
  };

  const handleAddPipelineStatus = async () => {
    if (!newStatusName.trim()) return;
    try {
      await createPipelineStatus({
        name: newStatusName,
        color: newStatusColor,
        order: pipeline.length
      });
      setNewStatusName('');
      setNewStatusColor('#64748b');
      onPipelineUpdate();
    } catch (error) {
      alert('Erro ao criar status');
    }
  };

  const handleDeletePipelineStatus = async (statusId) => {
    if (!window.confirm('Tem certeza que deseja excluir este status?')) return;
    try {
      await deletePipelineStatus(statusId);
      onPipelineUpdate();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao excluir status');
    }
  };

  const handleUpdatePipelineStatus = async (statusId, data) => {
    try {
      await updatePipelineStatus(statusId, data);
      onPipelineUpdate();
      setEditingStatus(null);
    } catch (error) {
      alert('Erro ao atualizar status');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats - Mobile Optimized */}
      <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
        <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-4 shadow-sm border border-slate-100">
          <p className="text-lg sm:text-2xl font-bold text-slate-800">{contacts.length}</p>
          <p className="text-xs sm:text-sm text-slate-500">Total</p>
        </div>
        {pipeline.slice(0, 5).map(status => (
          <div 
            key={status.id}
            className="rounded-lg sm:rounded-xl p-2 sm:p-4 border"
            style={{ 
              backgroundColor: `${status.color}15`,
              borderColor: `${status.color}30`
            }}
          >
            <p className="text-lg sm:text-2xl font-bold" style={{ color: status.color }}>
              {getContactsByStatus(status.id).length}
            </p>
            <p className="text-xs sm:text-sm truncate" style={{ color: status.color }}>{status.name}</p>
          </div>
        ))}
      </div>

      {/* Actions - Single Row Layout */}
      <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-sm">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Action Buttons */}
          <Button onClick={onNewContact} className="bg-teal-600 hover:bg-teal-700 text-white text-sm">
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            <span>Novo</span>
          </Button>
          <Button onClick={handleImport} variant="outline" disabled={importing} className="text-sm">
            <Download className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{importing ? 'Importando...' : 'Importar'}</span>
            <span className="sm:hidden">{importing ? '...' : 'Imp.'}</span>
          </Button>
          <Button onClick={() => setPipelineModalOpen(true)} variant="outline" className="text-sm">
            <Settings className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Configurar Funil</span>
            <span className="sm:hidden">Funil</span>
          </Button>
          
          {/* Separator */}
          <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" />
          
          {/* Multi-Status Filter */}
          <Popover open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 sm:gap-2 border border-slate-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm bg-white hover:bg-slate-50 transition-colors">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="truncate max-w-[80px] sm:max-w-[120px]">
                  {selectedStatuses.length === 0 
                    ? 'Todos' 
                    : selectedStatuses.length === 1
                      ? pipeline.find(s => s.id === selectedStatuses[0])?.name || '1 sel.'
                      : `${selectedStatuses.length} sel.`
                  }
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  <div className="flex items-center justify-between px-2 py-1 mb-1">
                    <span className="text-xs font-medium text-slate-500">Filtrar por Status</span>
                    {selectedStatuses.length > 0 && (
                      <button 
                        onClick={clearStatusSelection}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  {pipeline.map(status => (
                    <label
                      key={status.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedStatuses.includes(status.id)}
                        onCheckedChange={() => toggleStatusFilter(status.id)}
                      />
                      <div 
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: status.color }}
                      />
                      <span className="text-sm truncate">{status.name}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Contact Date Filter */}
            <Popover open={contactFilterOpen} onOpenChange={setContactFilterOpen}>
              <PopoverTrigger asChild>
                <button className={`flex items-center gap-1.5 sm:gap-2 border rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm transition-colors ${
                  selectedContactFilters.length > 0 
                    ? 'border-teal-300 bg-teal-50 text-teal-700' 
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}>
                  <Clock className="w-4 h-4" />
                  <span className="truncate max-w-[80px] sm:max-w-[120px]">
                    {selectedContactFilters.length === 0 
                      ? 'Contato' 
                      : selectedContactFilters.length === 1
                        ? contactFilterOptions.find(o => o.id === selectedContactFilters[0])?.label.split(' ')[0] || '1 sel.'
                        : `${selectedContactFilters.length} sel.`
                    }
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  <div className="flex items-center justify-between px-2 py-1 mb-1">
                    <span className="text-xs font-medium text-slate-500">Filtrar por Contato</span>
                    {selectedContactFilters.length > 0 && (
                      <button 
                        onClick={clearContactFilters}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  {contactFilterOptions.map(option => (
                    <label
                      key={option.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedContactFilters.includes(option.id)}
                        onCheckedChange={() => toggleContactFilter(option.id)}
                      />
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        option.color === 'emerald' ? 'bg-emerald-500' :
                        option.color === 'blue' ? 'bg-blue-500' :
                        option.color === 'purple' ? 'bg-purple-500' :
                        option.color === 'amber' ? 'bg-amber-500' :
                        'bg-red-500'
                      }`} />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Search */}
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 sm:pl-9 w-full sm:w-48 text-sm"
              />
            </div>
            
            {/* View Mode */}
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-sm"
            >
              <option value="kanban">Kanban</option>
              <option value="list">Lista</option>
            </select>
            
            {/* Hide Empty - Desktop only */}
            {viewMode === 'kanban' && (
              <label className="hidden sm:flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideEmpty}
                  onChange={(e) => setHideEmpty(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Ocultar vazios
              </label>
            )}
            
            {/* Save/Clear Filters */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleSaveFilters}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  hasSavedFilters 
                    ? 'text-teal-600 bg-teal-50 hover:bg-teal-100' 
                    : 'text-slate-400 hover:text-teal-600 hover:bg-teal-50'
                }`}
                title="Salvar filtros atuais"
              >
                <Save className="w-4 h-4" />
              </button>
              {hasSavedFilters && (
                <button
                  onClick={handleClearFilters}
                  className="p-1.5 sm:p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Limpar filtros salvos"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Separator */}
            <div className="hidden sm:block w-px h-6 bg-slate-200 mx-1" />
            
            {/* Manual Refresh & Connection Status */}
            <div className="flex items-center gap-1">
              <button
                onClick={onManualRefresh}
                className="p-1.5 sm:p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                title="Atualizar CRM"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <div 
                className={`p-1.5 sm:p-2 rounded-lg ${wsConnected ? 'text-emerald-500' : 'text-slate-300'}`}
                title={wsConnected ? 'Tempo real ativo' : 'Tempo real desconectado'}
              >
                {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              </div>
            </div>
          </div>
      </div>

      {/* Kanban View with Drag and Drop - Mobile Horizontal Scroll */}
      {viewMode === 'kanban' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0">
            <div 
              className="grid gap-2 sm:gap-3" 
              style={{ 
                gridTemplateColumns: `repeat(${pipeline.filter(s => (!hideEmpty || getContactsByStatus(s.id).length > 0) && (selectedStatuses.length === 0 || selectedStatuses.includes(s.id))).length}, minmax(160px, 1fr))`,
                minWidth: `${pipeline.filter(s => (!hideEmpty || getContactsByStatus(s.id).length > 0) && (selectedStatuses.length === 0 || selectedStatuses.includes(s.id))).length * 180}px`
              }}
            >
            {pipeline.map(status => {
              // Apply both search and status filter
              let statusContacts = getContactsByStatus(status.id);
              if (searchTerm) {
                statusContacts = statusContacts.filter(c => 
                  c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  c.phone.includes(searchTerm) ||
                  c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  c.summary?.toLowerCase().includes(searchTerm.toLowerCase())
                );
              }
              // Apply contact date filter
              if (selectedContactFilters.length > 0) {
                statusContacts = statusContacts.filter(c => matchesContactDateFilter(c, selectedContactFilters));
              }
              // If filtering by specific statuses, only show those columns
              if (selectedStatuses.length > 0 && !selectedStatuses.includes(status.id)) {
                return null;
              }
              // If hideEmpty is on and no contacts, skip
              if (hideEmpty && statusContacts.length === 0) {
                return null;
              }
              
              return (
                <DroppableColumn
                  key={status.id}
                  status={status}
                  contacts={statusContacts}
                  sourceOptions={sourceOptions}
                  pipeline={pipeline}
                  onOpenContact={onOpenContact}
                  onDeleteContact={handleDeleteContact}
                  onCreateTask={onCreateTaskFromContact}
                  onMarkContacted={handleMarkContacted}
                  onStatusChange={handleStatusChange}
                />
              );
            })}
            </div>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeContact ? (
              <div className="bg-white rounded-lg p-3 shadow-xl border-2 border-teal-500 w-64 rotate-3">
                <p className="font-medium text-slate-800">{activeContact.name}</p>
                <p className="text-xs text-slate-500">{activeContact.phone}</p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        /* List View - Mobile Optimized */
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm overflow-hidden">
          {filteredContacts.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-slate-500">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm sm:text-base">Nenhum contato encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredContacts.map(contact => {
                const status = getStatusById(contact.status_id || contact.status);
                const potentialValue = contact.total_quotes_value || contact.potential_value || 0;
                const formatDate = (dateStr) => {
                  if (!dateStr) return '';
                  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                };
                
                return (
                  <div 
                    key={contact.id} 
                    className="p-3 sm:p-4 hover:bg-slate-50 transition-colors cursor-pointer group"
                    onClick={() => onOpenContact(contact)}
                  >
                    <div className="flex items-start gap-2 sm:gap-4">
                      {/* Avatar with status color - Hidden on very small screens */}
                      <div 
                        className="hidden xs:flex w-10 h-10 sm:w-12 sm:h-12 rounded-full items-center justify-center flex-shrink-0" 
                        style={{ backgroundColor: `${status?.color || '#64748b'}20` }}
                      >
                        <User className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: status?.color || '#64748b' }} />
                      </div>
                      
                      {/* Main Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                          <p className="font-semibold text-slate-800 text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">{contact.name}</p>
                          <span 
                            className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium text-white flex-shrink-0"
                            style={{ backgroundColor: status?.color || '#64748b' }}
                          >
                            {status?.name || contact.status}
                          </span>
                          {contact.source && contact.source !== 'manual' && (
                            <span className="hidden sm:inline px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs flex-shrink-0">
                              {sourceOptions.find(s => s.value === contact.source)?.label || contact.source}
                            </span>
                          )}
                        </div>
                        
                        {/* Contact Info Row */}
                        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 mb-1.5 sm:mb-2 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            <span className="truncate max-w-[100px] sm:max-w-none">{contact.phone}</span>
                          </span>
                          {contact.company && (
                            <span className="hidden sm:flex items-center gap-1">
                              <Building className="w-3.5 h-3.5" />
                              <span className="truncate max-w-[150px]">{contact.company}</span>
                            </span>
                          )}
                          {(contact.city || contact.state) && (
                            <span className="hidden md:flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {contact.city}{contact.city && contact.state ? '/' : ''}{contact.state}
                            </span>
                          )}
                        </div>
                        
                        {/* Summary - Highlighted - Mobile Compact */}
                        {contact.summary && (
                          <div className="p-1.5 sm:p-2 bg-amber-50 rounded-lg border-l-2 sm:border-l-3 border-amber-400 mb-1.5 sm:mb-2">
                            <p className="text-xs sm:text-sm text-amber-800 flex items-start gap-1">
                              <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-1 sm:line-clamp-2">{contact.summary}</span>
                            </p>
                          </div>
                        )}
                        
                        {/* Footer Row - Metadata */}
                        <div className="flex items-center gap-2 sm:gap-4 text-xs text-slate-400 flex-wrap">
                          {potentialValue > 0 && (
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                              {potentialValue.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}
                            </span>
                          )}
                          {contact.notes?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                              {contact.notes.length}
                            </span>
                          )}
                          {contact.total_quotes > 0 && (
                            <span className="hidden sm:flex items-center gap-1 text-teal-600">
                              <FileText className="w-3.5 h-3.5" />
                              {contact.total_quotes} orç.
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            {formatDate(contact.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions - Always visible on mobile */}
                      <div className="flex items-center gap-1 sm:gap-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {/* WhatsApp Desktop Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openWhatsAppDesktop(contact.phone);
                          }}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-green-500 hover:text-green-700 hover:bg-green-50"
                          title="Abrir no WhatsApp Desktop"
                          data-testid="list-whatsapp-desktop-btn"
                        >
                          <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        {/* Copy Phone Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyPhoneToClipboard(contact.phone, contact.name);
                          }}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-blue-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Copiar número"
                          data-testid="list-copy-phone-btn"
                        >
                          <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        {/* Contacted Today Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkContacted(contact);
                          }}
                          className={`h-7 w-7 sm:h-8 sm:w-8 p-0 ${
                            contact.last_contacted_at && new Date(contact.last_contacted_at).toDateString() === new Date().toDateString()
                              ? 'text-emerald-500 bg-emerald-50'
                              : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'
                          }`}
                          title={
                            contact.last_contacted_at && new Date(contact.last_contacted_at).toDateString() === new Date().toDateString()
                              ? 'Contatado hoje'
                              : 'Marcar como contatado hoje'
                          }
                          data-testid="list-mark-contacted-btn"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        {/* Status dropdown - Hidden on very small screens */}
                        <select
                          value={contact.status_id || contact.status}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleStatusChange(contact, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="hidden sm:block text-sm border rounded-lg px-2 py-1.5 bg-white"
                        >
                          {pipeline.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditContact(contact);
                          }}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                        >
                          <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteContact(contact);
                          }}
                          className="hidden sm:flex h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pipeline Configuration Modal - Mobile Optimized */}
      <Dialog open={pipelineModalOpen} onOpenChange={setPipelineModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Configurar Funil de Vendas</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 sm:space-y-4">
            <p className="text-xs sm:text-sm text-slate-500">
              Personalize as etapas do seu funil de vendas.
            </p>
            
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {pipeline.map((status) => (
                <div key={status.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <GripVertical className="w-4 h-4 text-slate-400" />
                  <div 
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: status.color }}
                  />
                  {editingStatus === status.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        defaultValue={status.name}
                        id={`status-name-${status.id}`}
                        className="flex-1 h-8"
                      />
                      <input
                        type="color"
                        defaultValue={status.color}
                        id={`status-color-${status.id}`}
                        className="w-8 h-8 rounded cursor-pointer"
                      />
                      <Button 
                        size="sm" 
                        onClick={() => {
                          const name = document.getElementById(`status-name-${status.id}`).value;
                          const color = document.getElementById(`status-color-${status.id}`).value;
                          handleUpdatePipelineStatus(status.id, { name, color });
                        }}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-medium">{status.name}</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingStatus(status.id)}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDeletePipelineStatus(status.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex items-center gap-2 pt-2 border-t">
              <Input
                placeholder="Nome do novo status..."
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                className="flex-1"
              />
              <input
                type="color"
                value={newStatusColor}
                onChange={(e) => setNewStatusColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Button onClick={handleAddPipelineStatus} disabled={!newStatusName.trim()}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmSection;
